import React, { Suspense } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { useUserContext } from "src/contexts/userContext";
// ⚠ СТАТИК импорт — нэвтрээгүй ажилтан QR уншуулахад chunk татах шаардлагагүй
//   байх ёстой. Талбар дээр сүлжээ сул, chunk олдохгүй бол цагаан хуудас гарна.
import KnowledgeQrView from "src/pages/knowledge/qr";

import "antd/dist/antd.min.css";
import "moment/locale/mn";
import { ConfigProvider } from "antd";
import mnMN from "antd/lib/locale/mn_MN";

import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
// import "primeicons/primeicons.css";

// Loader/Layout нь ЭХНИЙ зурагт шаардлагатай тул статик хэвээр.
import Loader from "src/components/layout/loader";
import Layout from "src/components/layout/layout";
import ChunkErrorBoundary from "src/components/ChunkErrorBoundary";
import { lazyRetry } from "src/utils/lazyRetry";

// ═══════════════════════════════════════════════════════════════════════
// CODE SPLITTING — ХЭСЭГЧИЛСЭН, БҮЛЭГЛЭСЭН
// ═══════════════════════════════════════════════════════════════════════
//
// ТҮҮХ:
//   1) Эхэндээ 20 модуль бүгд СТАТИК импорттой байсан → бүх код НЭГ
//      `main.js`-д (4.5 MB) орж, /me гэх мэт хөнгөн хуудас ч удаан нээгддэг.
//   2) Дараа нь 20-уланг lazy болгосон → main 787 KB болж хурдассан, ГЭВЧ
//      60 тусдаа chunk үүсч, аль нэг нь олдохгүй бол БҮХ АПП цагаан болдог
//      эмзэг байдал үүссэн (production дээр яг ийм гацалт гарсан).
//   3) Одоогийн шийдэл — ДУНДЫН ЗАМ:
//
//        • Ажилтны ӨДӨР ТУТМЫН зам (/me, аюул, 15А/5А асуумж, м-мэдлэг) нь
//          СТАТИК — main.js дотор. Эдгээр нь chunk татдаггүй тул сүлжээ,
//          deploy, кэшийн ямар ч байдалд ЭВДЭРЧ ЧАДАХГҮЙ. Ажилчдын 90%
//          зөвхөн эдгээрийг хэрэглэдэг.
//
//        • Мэргэжилтний ХҮНД модулиуд нь lazy — гэхдээ 15 тусдаа chunk биш,
//          `webpackChunkName`-ээр 3 БҮЛЭГ болгон нэгтгэсэн. Эвдрэх гадаргуу
//          60-аас 3 болж багасна.
//
//        • Бүх lazy импорт `lazyRetry`-аар дамжина: алдаа гармагц дахин
//          оролдож, шаардвал хуудсыг нэг удаа шинэчилж өөрөө сэргэнэ
//          (utils/lazyRetry.js).
//
//        • Бүгд `ChunkErrorBoundary` дотор — сэргээж чадаагүй ч цагаан
//          хуудас биш, ойлгомжтой мэдэгдэл гарна.
//
// ─── СТАТИК: ажилтны өдөр тутмын цөм (chunk татахгүй) ──────────────────
import MainRoute from "src/routers/main"; //      /me, /dashboard, /data-analysis
import HazardRoute from "src/routers/hazard"; //  аюулын бүртгэл
import HighRisk from "src/routers/high_risk"; //  15А/5А урьдчилсан шалгалт
import KnowledgeRoute from "src/routers/knowledge"; // минутын мэдлэг
import PublicRoute from "src/routers/public"; //  ЭМС-ийн бус хэрэглэгчийн БҮХ апп

// ─── БҮЛЭГ "hse": ХАБЭА мэргэжилтний модулиуд (нэг chunk) ──────────────
const RiskRoute = lazyRetry(() =>
  import(/* webpackChunkName: "hse" */ "src/routers/risk"),
);
const Incident = lazyRetry(() =>
  import(/* webpackChunkName: "hse" */ "src/routers/incident"),
);
const Inspection = lazyRetry(() =>
  import(/* webpackChunkName: "hse" */ "src/routers/inspection"),
);
const Accident = lazyRetry(() =>
  import(/* webpackChunkName: "hse" */ "src/routers/accident"),
);
const MorningRoute = lazyRetry(() =>
  import(/* webpackChunkName: "hse" */ "src/routers/morningxab"),
);
// Хоолны санал, гомдол (эрүүл ахуй) — SSO дээрх /hygiene/* цэс
const HygieneRoute = lazyRetry(() =>
  import(/* webpackChunkName: "hse" */ "src/routers/hygiene"),
);
// ЕРӨНХИЙ ХАБЭА-н санал, гомдол (ХЭМАБМТ-08 4.1.3) — /feedback/* цэс.
// ⚠ Хоолны модулиас ТУСДАА: тэр нь хоолны газар, үйлдвэрлэгчээр
//   бүтэцлэгдсэн, энэ нь дотоод нэгж рүү хуваарилагддаг.
const FeedbackRoute = lazyRetry(() =>
  import(/* webpackChunkName: "hse" */ "src/routers/feedback"),
);
// 71300 утасны AI яриа — вэб симулятор + дуудлагын жагсаалт (ХАБЭА хэлтэс)
const PhoneAiRoute = lazyRetry(() =>
  import(/* webpackChunkName: "hse" */ "src/routers/phoneAi"),
);
// Урамшуулал — чадамжийн үнэлгээ (сургалтын журам А/528, IV бүлэг)
const RewardRoute = lazyRetry(() =>
  import(/* webpackChunkName: "hse" */ "src/routers/reward"),
);
// Ажил зогсоох эрх (SWA) — зураглалын page7, Gap Excel FR-05
const StopWorkRoute = lazyRetry(() =>
  import(/* webpackChunkName: "hse" */ "src/routers/stopwork"),
);
const NaryadRoute = lazyRetry(() =>
  import(/* webpackChunkName: "hse" */ "src/routers/naryad"),
);
const DailyRoute = lazyRetry(() =>
  import(/* webpackChunkName: "hse" */ "src/routers/daily"),
);
const RateRoute = lazyRetry(() =>
  import(/* webpackChunkName: "hse" */ "src/routers/rate"),
);
const WorkerRate = lazyRetry(() =>
  import(/* webpackChunkName: "hse" */ "src/routers/workerrate"),
);

// ─── БҮЛЭГ "supply": ХХ / склад / зарлага / бүтээгдэхүүн (нэг chunk) ────
const XxRoute = lazyRetry(() =>
  import(/* webpackChunkName: "supply" */ "src/routers/xx"),
);
const SkladRoute = lazyRetry(() =>
  import(/* webpackChunkName: "supply" */ "src/routers/sklad"),
);
const RashodRoute = lazyRetry(() =>
  import(/* webpackChunkName: "supply" */ "src/routers/rashod"),
);
const ProductRoute = lazyRetry(() =>
  import(/* webpackChunkName: "supply" */ "src/routers/product"),
);

// ─── БҮЛЭГ "misc": ховор хэрэглэгддэг (нэг chunk) ──────────────────────
const Phone = lazyRetry(() =>
  import(/* webpackChunkName: "misc" */ "src/routers/phone71300"),
);
const AdminRoute = lazyRetry(() =>
  import(/* webpackChunkName: "misc" */ "src/routers/admin"),
);

/**
 * Модуль бүрийн эзэмшдэг замын угтвар.
 *
 * Тухайн замд ТОХИРОХ модулийг л render хийнэ. Lazy модулийн хувьд энэ нь
 * chunk-ийг цаг бусаар татахаас сэргийлнэ; статик модулийн хувьд илүүц
 * <Routes> mount хийхээс сэргийлнэ.
 *
 * ⚠ Router-т ШИНЭ зам нэмэхдээ энд угтварыг нь бүртгэнэ үү. Мартвал тухайн
 *   хуудас "хоосон" гарна (алдаа шидэхгүй тул анзаарагдахад хэцүү).
 *   Давхцал зөвшөөрөгдөнө: /dashboard-ыг main ба risk хоёул эзэмшинэ.
 */
const ROUTE_PREFIXES = [
  [MainRoute, ["/me", "/dashboard", "/data-analysis"]],
  [XxRoute, ["/xx"]],
  [SkladRoute, ["/xx"]],
  [DailyRoute, ["/daily"]],
  [RateRoute, ["/rate"]],
  [RiskRoute, ["/risk", "/dashboard"]],
  [ProductRoute, ["/product"]],
  [RashodRoute, ["/xx"]],
  [Phone, ["/phone"]],
  [Inspection, ["/inspection"]],
  [Incident, ["/incident", "/plan"]],
  [Accident, ["/accident"]],
  [WorkerRate, ["/rate"]],
  [HighRisk, ["/highrisk"]],
  [HazardRoute, ["/hazard"]],
  [NaryadRoute, ["/naryad"]],
  [KnowledgeRoute, ["/knowledge"]],
  [MorningRoute, ["/morningxab"]],
  [HygieneRoute, ["/hygiene"]],
  [FeedbackRoute, ["/feedback"]],
  [PhoneAiRoute, ["/phone-ai"]],
  [RewardRoute, ["/reward"]],
  [StopWorkRoute, ["/stopwork"]],
  [AdminRoute, ["/admin"]],
];

const Router = () => {
  const { user } = useUserContext();
  const { pathname } = useLocation();

  /**
   * НЭВТРЭЭГҮЙ ажилтанд харагдах ганц хуудас — тоног төхөөрөмжийн QR.
   *
   * ⚠ Доорх үндсэн мод нь `user?.loggedIn` -оор бүрэн хаагддаг тул энэ
   *   салаагүйгээр наалт уншуулсан хүн ЦАГААН ХУУДАС харна.
   *
   * ⚠ Layout (хажуугийн цэс, толгой) ЗУРАГДАХГҮЙ — цэс нь `user.usermenu`
   *   дээр тогтдог, нэвтрээгүй үед байхгүй. Талбар дээрх ажилтанд ямар ч
   *   байсан цэс хэрэггүй — нэг л зүйл хэрэгтэй: юу мөрдөх ёстой вэ.
   *
   * Backend нь `/xab/knowledge/qr/public/…`-аар ЗӨВХӨН зааврын гарчиг
   *   буцаадаг — жинхэнэ хамгаалалт тэнд, энд биш.
   */
  const isPublicQr = /^\/knowledge\/qr\/[^/]+$/.test(pathname);
  if (!user?.loggedIn && isPublicQr) {
    return (
      <ChunkErrorBoundary>
        <ConfigProvider locale={mnMN}>
          <Routes>
            <Route path="/knowledge/qr/:code" element={<KnowledgeQrView />} />
          </Routes>
        </ConfigProvider>
      </ChunkErrorBoundary>
    );
  }

  // Замд тохирох модулиуд. Аль нь ч тохирохгүй бол (үл мэдэгдэх зам)
  // MainRoute-ыг л ачаална — хоосон дэлгэц гарахаас сэргийлнэ.
  const matched = ROUTE_PREFIXES.filter(([, prefixes]) =>
    prefixes.some((p) => pathname === p || pathname.startsWith(p + "/")),
  );
  const active = matched.length ? matched : [[MainRoute, []]];
  return (
    <>
      {user?.loggedIn && (
        <ChunkErrorBoundary>
          <Suspense fallback={<Loader />}>
            <ConfigProvider locale={mnMN}>
              {user.is_emc ? (
                <Layout>
                  {active.map(([Mod], i) => (
                    <Mod key={i} />
                  ))}
                </Layout>
              ) : (
                <Layout>
                  <PublicRoute />
                </Layout>
              )}
            </ConfigProvider>
          </Suspense>
        </ChunkErrorBoundary>
      )}
    </>
  );
};

export default React.memo(Router);
